import Icon from '@forwardflow/ui/Icons'
import { Link, useLocation } from 'react-router-dom'
import { useFeatureFlag } from 'configcat-react'
import { useEffect, useRef, useState } from 'react'
import { Tour, TourProps } from 'antd'
import useGetUserRole from '@forwardflow/hooks/customHooks/useGetUserRole'
import useAuthUser from '@forwardflow/hooks/customHooks/useAuthUser'
import { useOperatorStore } from '@forwardflow/store/useOperatorStore'
import { useAppStore } from '@forwardflow/store'
import { PhoneRotaryIcon } from '@forwardflow/icons'

const SideNav = () => {
   const location = useLocation()
   const { data: user } = useAuthUser({ revalidateOnMount: true })
   const currentPageUrl = location.pathname
   const [isOperator, setIsOperator] = useState(false)
   const { value: isCampaignFeatureEnabled } = useFeatureFlag(
      'campaignFeature',
      false
   )
   const { currentOrganization } = useAppStore()
   const { data: userRoleData } = useGetUserRole()
   const isAgent = userRoleData?.roles.includes('Agent')
   const ref1 = useRef(null)

   const [open, setOpen] = useState<boolean>(
      !localStorage.getItem('isDashboardTourDone') &&
         currentPageUrl === '/dashboard'
   )

   const { toggleOperatorSection } = useOperatorStore()

   const steps: TourProps['steps'] = [
      {
         title: null,
         description: (
            <div className="flex items-center px-6">
               <Icon name="TourIcon" />
               <p className="p-6 font-semibold">
                  Create your first campaign and experience the magic of Forward
                  Flow in action!
               </p>
            </div>
         ),
         className: 'ff-tour-step',
         placement: 'left',
         closeIcon: false,
         target: () => ref1.current,
      },
   ]
   useEffect(() => {
      setOpen(
         !localStorage.getItem('isTourDone') && currentPageUrl === '/dashboard'
      )
   }, [currentPageUrl])

   const handleTourClose = () => {
      setOpen(false)
      localStorage.setItem('isTourDone', 'true')
   }
   useEffect(() => {
      if (
         user?.email === 'operator@forward-flow.com' ||
         user?.email === 'brannon@forward-flow.com' ||
         user?.email === 'engineered@forward-flow.com'
      ) {
         setIsOperator(true)
      }
   }, [user])

   useEffect(() => {
      const handleKeyPress = (e: KeyboardEvent) => {
         if (e.ctrlKey && e.key === '`') {
            e.preventDefault()
            toggleOperatorSection()
         }
      }

      window.addEventListener('keydown', handleKeyPress)
      return () => window.removeEventListener('keydown', handleKeyPress)
   }, [toggleOperatorSection])

   return (
      <>
         <aside className="self-center h-[calc(100vh-112px)] justify-center items-center px-11 hidden lg:!flex flex-col gap-y-5">
            <div className="flex flex-col justify-center h-full gap-y-5">
               {/* Dashboard/Home link - always show */}
               <Link
                  to={'/dashboard'}
                  className={`w-[42px] h-[42px] rounded flexc ${
                     currentPageUrl.includes('/dashboard') ? 'bg-white/10' : ''
                  }`}
               >
                  <Icon
                     name="home"
                     className={` ${
                        currentPageUrl.includes('/dashboard')
                           ? 'fill-white'
                           : 'fill-[#7E7E7E]'
                     }`}
                  />
               </Link>

               {/* Demo conversations - only for operators */}
               {isOperator &&
                  currentPageUrl.includes('/demo-conversations') && (
                     <Link
                        data-testid="conversations-link"
                        to={'/demo-conversations/'}
                        className={`w-[42px] h-[42px] rounded flexc ${
                           currentPageUrl.includes('/demo-conversations')
                              ? 'bg-white/10'
                              : ''
                        }`}
                     >
                        <Icon
                           name="Message"
                           className={` ${
                              currentPageUrl.includes('/demo-conversations')
                                 ? 'fill-white'
                                 : 'fill-[#7E7E7E]'
                           }`}
                        />
                     </Link>
                  )}

               {!isAgent && isCampaignFeatureEnabled && (
                  <Link
                     ref={ref1}
                     to={'/campaign'}
                     className={`w-[42px] h-[42px] rounded flexc ff-tour-step  ${
                        currentPageUrl.includes('/campaign')
                           ? 'bg-white/10'
                           : ''
                     }`}
                  >
                     <Icon
                        name="Announcement"
                        className={` ${
                           currentPageUrl.includes('/campaign')
                              ? 'fill-white'
                              : 'fill-[#7E7E7E]'
                        }`}
                     />
                  </Link>
               )}
               <Link
                  data-testid="conversations-link"
                  to={{
                     pathname: '/conversations',
                     search: currentOrganization
                        ? `organizationId=${currentOrganization}`
                        : '',
                  }}
                  className={`w-[42px] h-[42px] rounded flexc ${
                     currentPageUrl.includes('/conversations')
                        ? 'bg-white/10'
                        : ''
                  }`}
               >
                  <Icon
                     name="Message"
                     className={` ${
                        currentPageUrl.includes('/conversations')
                           ? 'fill-white'
                           : 'fill-[#7E7E7E]'
                     }`}
                  />
               </Link>
               <Link
                  to={
                     isAgent
                        ? '/settings/appointment-preference'
                        : '/settings/user-management'
                  }
                  className={`w-[42px] h-[42px] rounded flexc  ${
                     currentPageUrl.includes('/settings') ? 'bg-white/10' : ''
                  }`}
               >
                  <Icon
                     name="Gear"
                     className={` ${
                        currentPageUrl.includes('/settings')
                           ? 'fill-white'
                           : 'fill-[#7E7E7E]'
                     }`}
                  />
               </Link>
               <Link
                  to="/phone-number-management"
                  className={`w-[42px] h-[42px] rounded flexc  ${
                     currentPageUrl.includes('/phone-number-management')
                        ? 'bg-white/10'
                        : ''
                  }`}
               >
                  <PhoneRotaryIcon
                     className={` ${
                        currentPageUrl.includes('/phone-number-management')
                           ? 'text-white'
                           : 'text-[#7E7E7E]'
                     }`}
                  />
               </Link>
               {/* Regular app navigation - show when subscription is activated */}
               {/* {data?.onboardingProgress?.subscriptionActivated?.status ===
                  'completed' &&
                  !currentPageUrl.includes('/onboarding/setup') &&
                  !currentPageUrl.includes('/demo-conversations') && <></>} */}
            </div>
         </aside>
         <Tour
            open={open}
            onClose={handleTourClose}
            steps={steps}
            closeIcon={true}
            mask={{
               style: {
                  boxShadow: 'inset 0 0 15px #333',
               },
               color: 'rgba(24, 25, 29, 0.8)',
            }}
         />
      </>
   )
}
export default SideNav
